// "Plan changed" / "Plan cancelled" notifications (2026-10-29; mockup: "FriSo Text FriSo" canvas, boards 6–7).
// When the owner of a shared plan changes its date, time, place or title, or deletes it, everyone it's shared
// with hears about it: in the app (Calendar.js saveEdit / deleteEvent) and by text (functions/textReply.js, which
// gets this file through functions/sync-shared.js). Plain events only: Busy and Away blocks never notify, and
// notes edits stay quiet. No imports, so the server can use it as is.

const isPlan = (ev) => !!ev && (ev.eventKind || "event") === "event" && !ev.schedule;

// Which of title / date / time / place differ between two versions of a plan
export function planChanges(before, after) {
  const out = [];
  if ((before.title || "") !== (after.title || "")) out.push("title");
  if (before.startDate !== after.startDate || (before.endDate || before.startDate) !== (after.endDate || after.startDate)) out.push("date");
  if ((before.startTime || null) !== (after.startTime || null) || (before.endTime || null) !== (after.endTime || null)) out.push("time");
  if ((before.location || null) !== (after.location || null)) out.push("place");
  return out;
}

// event_changed notifications for everyone who can see the plan after the change, except the person who made it
// and anyone in `skip` (they were just sent "New event" for a newly added pod)
export function changeNotifs({ before, after, eventId, actorEmail, skip = [], now = Date.now() }) {
  const changes = planChanges(before, after);
  if (!isPlan(before) || !isPlan(after) || !changes.length) return [];
  return [...new Set(after.memberEmails || [])].filter((e) => e !== actorEmail && !skip.includes(e)).map((recipientEmail) => ({
    type: "event_changed", actorEmail, recipientEmail, eventId, changes,
    eventTitle: after.title || "", ...(changes.includes("title") && { oldTitle: before.title || "" }),
    startDate: after.startDate || null, startTime: after.startTime || null, location: after.location || null,
    status: "unread", createdAt: now,
  }));
}

// event_cancelled notifications when the owner deletes a shared plan (date = the date deleted; following = that
// date and the ones after, for a repeating plan)
export function cancelNotifs({ ev, date, following = false, actorEmail, now = Date.now() }) {
  if (!isPlan(ev)) return [];
  return [...new Set(ev.memberEmails || [])].filter((e) => e !== actorEmail).map((recipientEmail) => ({
    type: "event_cancelled", actorEmail, recipientEmail, eventTitle: ev.title || "",
    startDate: date || ev.startDate || null, startTime: ev.startTime || null, ...(following && { following: true }),
    status: "unread", createdAt: now,
  }));
}

const joinAnd = (xs) => (xs.length < 3 ? xs.join(" and ") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);

// The notification card's sentence: "Anna changed the time of “Dinner”", "Anna renamed “Trivia” to “Pub quiz”
// and changed the place", "Anna cancelled “Dinner”"
export function planNotifText(n, actorName) {
  if (n.type === "event_cancelled") return `${actorName} cancelled “${n.eventTitle}”${n.following ? " from this date on" : ""}`;
  const rest = (n.changes || []).filter((c) => c !== "title");
  if (n.changes?.includes("title")) {
    return `${actorName} renamed “${n.oldTitle}” to “${n.eventTitle}”${rest.length ? ` and changed the ${joinAnd(rest)}` : ""}`;
  }
  return `${actorName} changed the ${joinAnd(rest)} of “${n.eventTitle}”`;
}
